import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/atoms";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { BusinessSettingsSection } from "@/redux/slices/onboarding/types";
import { SetupPanel, SetupPanelItem, WhyDoINeedModal, WalkthroughModal, ReadSetupGuideModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const BusinessSettingsPaths = {
  BUSINESS_DETAILS: "/config/business/business-details",
  WEBSITE_DETAILS: "/config/business/website-details",
  EMAIL_NOTIFICATION_SETTINGS: "/config/business/email-notifications",
  ACCESS_CONTROL: "/config/business/access-control",
  DISPLAY_OPTIONS: "/config/business/display-options",
} as const;

interface BusinessSettingsPanelProps {
  defaultOpen?: boolean;
}

enum ModalType {
  BUSINESS_DETAILS_WHY = "business-details-why",
  BUSINESS_DETAILS_WALKTHROUGH = "business-details-walkthrough",
  BUSINESS_DETAILS_DOCUMENTATION = "business-details-documentation",
  WEBSITE_DETAILS_WHY = "website-details-why",
  WEBSITE_DETAILS_WALKTHROUGH = "website-details-walkthrough",
  WEBSITE_DETAILS_DOCUMENTATION = "website-details-documentation",
  EMAIL_NOTIFICATIONS_WHY = "email-notifications-why",
  EMAIL_NOTIFICATIONS_WALKTHROUGH = "email-notifications-walkthrough",
  EMAIL_NOTIFICATIONS_DOCUMENTATION = "email-notifications-documentation",
  ACCESS_CONTROLS_WHY = "access-controls-why",
  ACCESS_CONTROLS_WALKTHROUGH = "access-controls-walkthrough",
  ACCESS_CONTROLS_DOCUMENTATION = "access-controls-documentation",
  DISPLAY_OPTIONS_WHY = "display-options-why",
  DISPLAY_OPTIONS_WALKTHROUGH = "display-options-walkthrough",
  DISPLAY_OPTIONS_DOCUMENTATION = "display-options-documentation",
}

export const BusinessSettingsPanel = ({ defaultOpen = false }: BusinessSettingsPanelProps) => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isBusinessDetailsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.businessSettings.businessDetails) : false;
  const isWebsiteDetailsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.businessSettings.websiteDetails) : false;
  const isEmailNotificationsCompleted = onboardingStatus
    ? isSectionComplete(onboardingStatus.businessSettings.emailNotificationSettings)
    : false;
  const isAccessControlsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.businessSettings.accessControl) : false;
  const isDisplayOptionsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.businessSettings.displayOptions) : false;

  const completedSteps = [
    isBusinessDetailsCompleted,
    isWebsiteDetailsCompleted,
    isEmailNotificationsCompleted,
    isAccessControlsCompleted,
    isDisplayOptionsCompleted,
  ].filter(Boolean).length;
  const totalSteps = 5;

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const businessDetailsFeaturedIcon = {
    name: "Briefcase",
    background: "bg-primary-light",
  };

  const websiteDetailsFeaturedIcon = {
    name: "Global",
    background: "bg-primary-light",
  };

  const emailNotificationsFeaturedIcon = {
    name: "Notification",
    background: "bg-primary-light",
  };

  const accessControlsFeaturedIcon = {
    name: "Setting4",
    background: "bg-primary-light",
  };

  const displayOptionsFeaturedIcon = {
    name: "Eye",
    background: "bg-primary-light",
  };

  const handleUpdateDetails = (path: string, section: BusinessSettingsSection) => {
    setQuickSetupFlag(section);
    navigate(path);

    if (onboardingStatus) {
      const sectionMap = {
        [BusinessSettingsSection.BUSINESS_DETAILS]: onboardingStatus.businessSettings.businessDetails,
        [BusinessSettingsSection.WEBSITE_DETAILS]: onboardingStatus.businessSettings.websiteDetails,
        [BusinessSettingsSection.EMAIL_NOTIFICATION_SETTINGS]: onboardingStatus.businessSettings.emailNotificationSettings,
        [BusinessSettingsSection.ACCESS_CONTROL]: onboardingStatus.businessSettings.accessControl,
        [BusinessSettingsSection.DISPLAY_OPTIONS]: onboardingStatus.businessSettings.displayOptions,
      };

      const sectionStatus = sectionMap[section];

      if (sectionStatus && !isSectionComplete(sectionStatus)) {
        updateSectionStatus({ section, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  return (
    <>
      <SetupPanel
        stepNumber={2}
        title="Business Settings"
        description="Set the foundation for how your business appears and operates in the platform"
        stepCount={totalSteps}
        completedSteps={completedSteps}
        defaultOpen={defaultOpen}
      >
        <SetupPanelItem
          icon={businessDetailsFeaturedIcon.name}
          iconBackground={businessDetailsFeaturedIcon.background}
          title="Business Details"
          isCompleted={isBusinessDetailsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleUpdateDetails(BusinessSettingsPaths.BUSINESS_DETAILS, BusinessSettingsSection.BUSINESS_DETAILS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.BUSINESS_DETAILS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.BUSINESS_DETAILS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.BUSINESS_DETAILS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon={websiteDetailsFeaturedIcon.name}
          iconBackground={websiteDetailsFeaturedIcon.background}
          title="Website Details"
          isCompleted={isWebsiteDetailsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleUpdateDetails(BusinessSettingsPaths.WEBSITE_DETAILS, BusinessSettingsSection.WEBSITE_DETAILS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.WEBSITE_DETAILS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.WEBSITE_DETAILS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.WEBSITE_DETAILS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon={emailNotificationsFeaturedIcon.name}
          iconBackground={emailNotificationsFeaturedIcon.background}
          title="Email Notification Settings"
          isCompleted={isEmailNotificationsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () =>
                handleUpdateDetails(BusinessSettingsPaths.EMAIL_NOTIFICATION_SETTINGS, BusinessSettingsSection.EMAIL_NOTIFICATION_SETTINGS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.EMAIL_NOTIFICATIONS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.EMAIL_NOTIFICATIONS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.EMAIL_NOTIFICATIONS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon={accessControlsFeaturedIcon.name}
          iconBackground={accessControlsFeaturedIcon.background}
          title="Configure Access Controls"
          isCompleted={isAccessControlsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleUpdateDetails(BusinessSettingsPaths.ACCESS_CONTROL, BusinessSettingsSection.ACCESS_CONTROL),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.ACCESS_CONTROLS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.ACCESS_CONTROLS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.ACCESS_CONTROLS_DOCUMENTATION) },
          ]}
        />

        <SetupPanelItem
          icon={displayOptionsFeaturedIcon.name}
          iconBackground={displayOptionsFeaturedIcon.background}
          title="Display Options"
          isCompleted={isDisplayOptionsCompleted}
          actions={[
            {
              label: "Update Details",
              onClick: () => handleUpdateDetails(BusinessSettingsPaths.DISPLAY_OPTIONS, BusinessSettingsSection.DISPLAY_OPTIONS),
              variant: "primary",
            },
          ]}
          helpOptions={[
            {
              label: "Why do I need to do this?",
              icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
              onClick: () => setActiveModal(ModalType.DISPLAY_OPTIONS_WHY),
            },
            { label: "Watch walkthrough video", icon: "Video", onClick: () => setActiveModal(ModalType.DISPLAY_OPTIONS_WALKTHROUGH) },
            { label: "Read Setup Guide", icon: "Document", onClick: () => setActiveModal(ModalType.DISPLAY_OPTIONS_DOCUMENTATION) },
          ]}
        />
      </SetupPanel>

      {/* Business Details Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.BUSINESS_DETAILS_WHY}
        onClose={handleModalClose}
        title="Why do I need to configure Business Details?"
        featuredIcon={businessDetailsFeaturedIcon}
        explanations={quickSetupExplanations.businessSettings.businessDetails}
      />

      <WalkthroughModal
        featuredIcon={businessDetailsFeaturedIcon}
        open={activeModal === ModalType.BUSINESS_DETAILS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Business Details Setup)"
        videoUrl={quickSetupContent.businessSettings.businessDetails.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={businessDetailsFeaturedIcon}
        open={activeModal === ModalType.BUSINESS_DETAILS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Business Details Setup Guide"
        documentationUrl={quickSetupContent.businessSettings.businessDetails.documentationUrl}
        pdfUrl={quickSetupContent.businessSettings.businessDetails.pdfUrl}
      />

      {/* Website Details Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.WEBSITE_DETAILS_WHY}
        onClose={handleModalClose}
        title="Why do I need to configure Website Details?"
        featuredIcon={websiteDetailsFeaturedIcon}
        explanations={quickSetupExplanations.businessSettings.websiteDetails}
      />

      <WalkthroughModal
        featuredIcon={websiteDetailsFeaturedIcon}
        open={activeModal === ModalType.WEBSITE_DETAILS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Website Details Configuration)"
        videoUrl={quickSetupContent.businessSettings.websiteDetails.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={websiteDetailsFeaturedIcon}
        open={activeModal === ModalType.WEBSITE_DETAILS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Website Details Setup Guide"
        documentationUrl={quickSetupContent.businessSettings.websiteDetails.documentationUrl}
        pdfUrl={quickSetupContent.businessSettings.websiteDetails.pdfUrl}
      />

      {/* Email Notifications Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.EMAIL_NOTIFICATIONS_WHY}
        onClose={handleModalClose}
        title="Why configure Email Notifications?"
        featuredIcon={emailNotificationsFeaturedIcon}
        explanations={quickSetupExplanations.businessSettings.emailNotifications}
      />

      <WalkthroughModal
        featuredIcon={emailNotificationsFeaturedIcon}
        open={activeModal === ModalType.EMAIL_NOTIFICATIONS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Email Notification Setup)"
        videoUrl={quickSetupContent.businessSettings.emailNotifications.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={emailNotificationsFeaturedIcon}
        open={activeModal === ModalType.EMAIL_NOTIFICATIONS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Email Notification Setup Guide"
        documentationUrl={quickSetupContent.businessSettings.emailNotifications.documentationUrl}
        pdfUrl={quickSetupContent.businessSettings.emailNotifications.pdfUrl}
      />

      {/* Access Controls Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.ACCESS_CONTROLS_WHY}
        onClose={handleModalClose}
        title="Why configure Access Controls?"
        featuredIcon={accessControlsFeaturedIcon}
        explanations={quickSetupExplanations.businessSettings.accessControls}
      />

      <WalkthroughModal
        featuredIcon={accessControlsFeaturedIcon}
        open={activeModal === ModalType.ACCESS_CONTROLS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Access Controls Configuration)"
        videoUrl={quickSetupContent.businessSettings.accessControls.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={accessControlsFeaturedIcon}
        open={activeModal === ModalType.ACCESS_CONTROLS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Access Controls Setup Guide"
        documentationUrl={quickSetupContent.businessSettings.accessControls.documentationUrl}
        pdfUrl={quickSetupContent.businessSettings.accessControls.pdfUrl}
      />

      {/* Display Options Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.DISPLAY_OPTIONS_WHY}
        onClose={handleModalClose}
        title="Why configure Display Options?"
        featuredIcon={displayOptionsFeaturedIcon}
        explanations={quickSetupExplanations.businessSettings.displayOptions}
      />

      <WalkthroughModal
        featuredIcon={displayOptionsFeaturedIcon}
        open={activeModal === ModalType.DISPLAY_OPTIONS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Display Options Setup)"
        videoUrl={quickSetupContent.businessSettings.displayOptions.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={displayOptionsFeaturedIcon}
        open={activeModal === ModalType.DISPLAY_OPTIONS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Display Options Setup Guide"
        documentationUrl={quickSetupContent.businessSettings.displayOptions.documentationUrl}
        pdfUrl={quickSetupContent.businessSettings.displayOptions.pdfUrl}
      />
    </>
  );
};
